<template>
  <button
    type="button"
    class="exodia-overlay"
    :class="[phase, { compact: duelCompact }]"
    @click="skip"
  >
    <div class="stage">
      <div class="void" />
      <div class="glyphs" />
      <div class="embers" aria-hidden="true">
        <span
          v-for="ember in EMBERS"
          :key="ember.i"
          :style="{
            left: ember.x + '%',
            '--delay': ember.delay + 's',
            '--dur': ember.dur + 's',
          }"
        />
      </div>
      <div class="vignette" />
      <div class="seal" aria-hidden="true">
        <span class="ring outer" />
        <span class="ring inner" />
        <span class="spoke" v-for="n in 5" :key="n" :style="{ '--i': n }" />
      </div>
      <svg class="penta" viewBox="0 0 100 100" aria-hidden="true">
        <polygon
          points="50,3 61,35 97,35 68,57 79,93 50,73 21,93 32,57 3,35 39,35"
        />
      </svg>
      <div class="bolts" aria-hidden="true" />

      <div class="assembly">
        <figure
          v-for="piece in PIECES"
          :key="piece.id"
          class="piece"
          :class="piece.slot"
        >
          <img
            :src="getLocalCardImageUrl(piece.id)"
            :alt="piece.name"
          />
        </figure>
      </div>

      <div class="flash" />
      <div class="flare" />

      <div class="titles">
        <p class="kicker">{{ t('duel.exodia.kicker') }}</p>
        <p class="name">EXODIA</p>
        <p class="sub">{{ t('duel.exodia.sub') }}</p>
      </div>
    </div>

    <p v-if="skipArmed" class="skip-hint">{{ t('duel.exodia.skip') }}</p>
  </button>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from '../i18n/t'
import { useCompactLayout } from '../composables/useCompactLayout'
import { DuelSfx } from '../game/audio/DuelSfx'
import { getLocalCardImageUrl } from '../utils/cardImages'

// Exodia faces the viewer, so his left limbs sit on the viewer's right.
const PIECES = [
  { id: '7902349', slot: 'right-arm', name: 'Left Arm' },
  { id: '70903634', slot: 'left-arm', name: 'Right Arm' },
  { id: '33396948', slot: 'head', name: 'Exodia' },
  { id: '44519536', slot: 'right-leg', name: 'Left Leg' },
  { id: '8124921', slot: 'left-leg', name: 'Right Leg' },
] as const

const EMBERS = Array.from({ length: 20 }, (_, i) => ({
  i,
  x: 4 + ((i * 19) % 92),
  delay: (i * 0.17) % 2.2,
  dur: 2.2 + (i % 6) * 0.35,
}))

/** Full cinematic, then the game-over dialog. */
const EXODIA_CINEMATIC_MS = 7200

/**
 * The search that completed Exodia is still under the finger. A click in
 * this window used to skip the assembly before the legs moved.
 */
const SKIP_ARM_MS = 700

const props = withDefaults(
  defineProps<{
    won?: boolean
  }>(),
  { won: true },
)

const emit = defineEmits<{
  done: []
}>()

const { t } = useI18n()
const { duelCompact } = useCompactLayout()
const phase = ref<'in' | 'awake' | 'title'>('in')
const skipArmed = ref(false)

let finished = false
const timers: ReturnType<typeof setTimeout>[] = []

function finish(): void {
  if (finished) return
  finished = true
  for (const timer of timers) clearTimeout(timer)
  emit('done')
}

function skip(): void {
  if (!skipArmed.value) return
  finish()
}

function later(ms: number, fn: () => void): void {
  timers.push(setTimeout(fn, ms))
}

onMounted(() => {
  later(SKIP_ARM_MS, () => {
    skipArmed.value = true
  })
  DuelSfx.play('packWhoosh')
  later(380, () => DuelSfx.play('set'))
  later(920, () => DuelSfx.play('set'))
  later(1480, () => DuelSfx.play('summon'))
  later(2550, () => {
    phase.value = 'awake'
    DuelSfx.play('impact')
  })
  later(3600, () => {
    phase.value = 'title'
    DuelSfx.play(props.won ? 'win' : 'lose')
  })
  later(EXODIA_CINEMATIC_MS, finish)
})

onUnmounted(() => {
  for (const timer of timers) clearTimeout(timer)
})
</script>

<style scoped>
.exodia-overlay {
  position: fixed;
  inset: 0;
  z-index: 12000;
  margin: 0;
  padding: 0;
  border: 0;
  overflow: hidden;
  cursor: pointer;
  color: #fde68a;
  font: inherit;
  background: #020103;
}

.stage {
  position: absolute;
  inset: 0;
}

.in .stage {
  animation: none;
}

.awake .stage {
  animation: quake 0.62s cubic-bezier(0.22, 0.8, 0.32, 1) both;
}

.void {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse at 50% 38%, rgba(150, 12, 8, 0.7), transparent 56%),
    radial-gradient(ellipse at 50% 88%, rgba(232, 197, 71, 0.18), transparent 48%),
    #020103;
}

.awake .void,
.title .void {
  background:
    radial-gradient(ellipse at 50% 40%, rgba(190, 28, 12, 0.82), transparent 60%),
    radial-gradient(ellipse at 50% 78%, rgba(255, 214, 74, 0.28), transparent 52%),
    #120204;
}

.glyphs {
  position: absolute;
  inset: -20%;
  opacity: 0.22;
  background-image:
    repeating-linear-gradient(
      90deg,
      transparent 0 46px,
      rgba(232, 197, 71, 0.16) 46px 48px
    ),
    repeating-linear-gradient(
      0deg,
      transparent 0 62px,
      rgba(232, 197, 71, 0.12) 62px 64px
    );
  animation: glyphs-drift 16s linear infinite;
  pointer-events: none;
}

.embers {
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
}

.embers span {
  position: absolute;
  bottom: -8%;
  width: 3px;
  height: 18px;
  border-radius: 2px;
  background: linear-gradient(
    180deg,
    rgba(255, 248, 220, 0.95),
    rgba(232, 80, 20, 0.15)
  );
  opacity: 0;
  animation: ember-rise var(--dur) var(--delay) linear infinite;
}

.vignette {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: radial-gradient(
    ellipse at 50% 46%,
    transparent 22%,
    rgba(0, 0, 0, 0.78) 100%
  );
}

.seal {
  position: absolute;
  left: 50%;
  top: 46%;
  width: min(96vmin, 900px);
  height: min(96vmin, 900px);
  transform: translate(-50%, -50%) scale(0.4);
  opacity: 0;
  pointer-events: none;
}

.in .seal {
  animation: seal-in 1.15s 0.08s cubic-bezier(0.16, 1.2, 0.32, 1) both;
}

.awake .seal,
.title .seal {
  opacity: 1;
  transform: translate(-50%, -50%) scale(1);
}

.ring {
  position: absolute;
  inset: 8%;
  border-radius: 50%;
  border: 2px solid rgba(232, 197, 71, 0.55);
  box-shadow:
    0 0 28px rgba(232, 197, 71, 0.35),
    inset 0 0 40px rgba(232, 197, 71, 0.12);
}

.ring.inner {
  inset: 22%;
  border-width: 1px;
  animation: seal-spin 22s linear infinite reverse;
}

.ring.outer {
  animation: seal-spin 28s linear infinite;
}

.spoke {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 2px;
  height: 46%;
  margin-left: -1px;
  transform-origin: 50% 0;
  transform: rotate(calc(var(--i) * 72deg));
  background: linear-gradient(
    180deg,
    rgba(232, 197, 71, 0.85),
    transparent 78%
  );
}

.penta {
  position: absolute;
  left: 50%;
  top: 46%;
  width: min(78vmin, 720px);
  height: min(78vmin, 720px);
  transform: translate(-50%, -50%);
  pointer-events: none;
  opacity: 0;
  filter: drop-shadow(0 0 18px rgba(255, 210, 80, 0.7));
}

.penta polygon {
  fill: rgba(232, 197, 71, 0.05);
  stroke: rgba(255, 224, 120, 0.9);
  stroke-width: 0.7;
  stroke-linejoin: round;
}

.in .penta {
  animation: penta-draw 1.6s 0.4s ease-out both;
}

.awake .penta,
.title .penta {
  opacity: 0.85;
}

.bolts {
  position: absolute;
  inset: 10% 8%;
  pointer-events: none;
  opacity: 0;
  background:
    linear-gradient(
      115deg,
      transparent 42%,
      rgba(255, 248, 220, 0.6) 50%,
      transparent 58%
    ),
    linear-gradient(
      -115deg,
      transparent 42%,
      rgba(255, 210, 80, 0.45) 50%,
      transparent 58%
    );
  mix-blend-mode: screen;
}

.awake .bolts {
  animation: bolts-flash 0.55s ease-out both;
}

.title .bolts {
  opacity: 0.22;
}

.assembly {
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
}

.piece {
  position: absolute;
  margin: 0;
  opacity: 0;
  filter: drop-shadow(0 0 22px rgba(232, 197, 71, 0.55));
}

.piece img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 10px;
  box-shadow:
    0 0 0 2px rgba(232, 197, 71, 0.75),
    0 18px 44px rgba(0, 0, 0, 0.7);
}

.piece.head {
  z-index: 5;
  left: 50%;
  top: 1%;
  width: min(34vw, 48vh);
  height: min(49.6vw, 70vh);
  transform: translate(-50%, -120%);
}

.piece.left-arm {
  z-index: 3;
  left: 2%;
  top: 20%;
  width: min(24vw, 36vh);
  height: min(35vw, 52vh);
  transform: translateX(-140%);
}

.piece.right-arm {
  z-index: 3;
  right: 2%;
  top: 20%;
  width: min(24vw, 36vh);
  height: min(35vw, 52vh);
  transform: translateX(140%);
}

.piece.left-leg {
  z-index: 1;
  left: 11%;
  bottom: 2%;
  width: min(24vw, 36vh);
  height: min(35vw, 52vh);
  transform: translateY(140%);
}

.piece.right-leg {
  z-index: 1;
  right: 11%;
  bottom: 2%;
  width: min(24vw, 36vh);
  height: min(35vw, 52vh);
  transform: translateY(140%);
}

.in .piece.left-leg {
  animation: slam-leg 0.72s 0.32s cubic-bezier(0.12, 1.32, 0.32, 1) both;
}

.in .piece.right-leg {
  animation: slam-leg 0.72s 0.52s cubic-bezier(0.12, 1.32, 0.32, 1) both;
}

.in .piece.left-arm {
  animation: slam-arm-l 0.72s 0.92s cubic-bezier(0.12, 1.32, 0.32, 1) both;
}

.in .piece.right-arm {
  animation: slam-arm-r 0.72s 1.12s cubic-bezier(0.12, 1.32, 0.32, 1) both;
}

.in .piece.head {
  animation: slam-head 0.95s 1.55s cubic-bezier(0.1, 1.38, 0.32, 1) both;
}

.awake .piece,
.title .piece {
  opacity: 1;
  transform: none;
  animation: none;
}

.awake .piece.head,
.title .piece.head {
  transform: translate(-50%, 0);
  filter:
    drop-shadow(0 0 32px rgba(255, 236, 150, 1))
    drop-shadow(0 0 80px rgba(232, 197, 71, 0.85));
}

.title .piece.head {
  animation: head-pulse 1.8s ease-in-out infinite;
}

.flash {
  position: absolute;
  inset: 0;
  z-index: 3;
  pointer-events: none;
  opacity: 0;
  background: radial-gradient(
    circle at 50% 42%,
    rgba(255, 248, 220, 0.95),
    rgba(232, 197, 71, 0.32) 38%,
    transparent 70%
  );
}

.awake .flash {
  animation: exodia-flash 0.75s ease-out both;
}

.flare {
  position: absolute;
  inset: 0;
  z-index: 3;
  pointer-events: none;
  opacity: 0;
  background:
    radial-gradient(circle at 50% 44%, rgba(255, 240, 160, 0.35), transparent 42%);
  mix-blend-mode: screen;
}

.title .flare {
  opacity: 1;
  animation: flare-breathe 2.4s ease-in-out infinite;
}

.titles {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 7%;
  z-index: 4;
  text-align: center;
  pointer-events: none;
  opacity: 0;
}

.title .titles {
  animation: title-in 0.75s cubic-bezier(0.16, 1.2, 0.32, 1) both;
}

.kicker {
  margin: 0 0 6px;
  font-size: clamp(11px, 1.6vw, 14px);
  font-weight: 700;
  letter-spacing: 0.5em;
  text-transform: uppercase;
  color: #fde68a;
}

.name {
  margin: 0;
  font-family: Cinzel, Palatino, serif;
  font-size: clamp(56px, 14vw, 148px);
  font-weight: 700;
  letter-spacing: 0.2em;
  line-height: 0.92;
  color: #fff8d4;
  text-shadow:
    0 0 28px rgba(232, 197, 71, 0.95),
    0 10px 36px rgba(0, 0, 0, 0.9);
}

.sub {
  margin: 10px 0 0;
  font-size: clamp(13px, 2.2vw, 20px);
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: #e8c547;
}

.skip-hint {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 16px;
  z-index: 5;
  margin: 0;
  font-size: 11px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #c6c5d0;
  opacity: 0.7;
}

.compact .piece.head {
  width: min(30vh, 46vw);
  height: min(44vh, 67vw);
  top: 1%;
}

.compact .piece.left-arm,
.compact .piece.right-arm,
.compact .piece.left-leg,
.compact .piece.right-leg {
  width: min(22vh, 32vw);
  height: min(32vh, 47vw);
}

.compact .piece.left-arm,
.compact .piece.right-arm {
  top: 18%;
}

.compact .name {
  letter-spacing: 0.08em;
  font-size: clamp(42px, 16vw, 88px);
}

.compact .titles {
  bottom: 8%;
}

@keyframes glyphs-drift {
  to {
    transform: translate3d(-48px, -64px, 0);
  }
}

@keyframes ember-rise {
  0% {
    opacity: 0;
    transform: translateY(0) scale(1);
  }
  12% {
    opacity: 0.9;
  }
  100% {
    opacity: 0;
    transform: translateY(-110vh) scale(0.4);
  }
}

@keyframes seal-in {
  from {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.32) rotate(-18deg);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
}

@keyframes seal-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes penta-draw {
  from {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.7) rotate(-12deg);
  }
  to {
    opacity: 0.7;
    transform: translate(-50%, -50%) scale(1);
  }
}

@keyframes slam-arm-l {
  0% {
    opacity: 0;
    transform: translateX(-150%) rotate(-8deg);
  }
  72% {
    opacity: 1;
    transform: translateX(4%) rotate(2deg);
  }
  100% {
    opacity: 1;
    transform: none;
  }
}

@keyframes slam-arm-r {
  0% {
    opacity: 0;
    transform: translateX(150%) rotate(8deg);
  }
  72% {
    opacity: 1;
    transform: translateX(-4%) rotate(-2deg);
  }
  100% {
    opacity: 1;
    transform: none;
  }
}

@keyframes slam-leg {
  0% {
    opacity: 0;
    transform: translateY(150%);
  }
  72% {
    opacity: 1;
    transform: translateY(-5%);
  }
  100% {
    opacity: 1;
    transform: none;
  }
}

@keyframes slam-head {
  0% {
    opacity: 0;
    transform: translate(-50%, -130%) scale(1.7);
  }
  70% {
    opacity: 1;
    transform: translate(-50%, 5%) scale(1.08);
  }
  100% {
    opacity: 1;
    transform: translate(-50%, 0) scale(1);
  }
}

@keyframes quake {
  0% {
    transform: translate(0, 0);
  }
  18% {
    transform: translate(-14px, 9px);
  }
  36% {
    transform: translate(12px, -8px);
  }
  54% {
    transform: translate(-8px, 6px);
  }
  100% {
    transform: translate(0, 0);
  }
}

@keyframes bolts-flash {
  0% {
    opacity: 0;
  }
  35% {
    opacity: 1;
  }
  100% {
    opacity: 0.14;
  }
}

@keyframes exodia-flash {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

@keyframes flare-breathe {
  0%,
  100% {
    opacity: 0.45;
  }
  50% {
    opacity: 0.9;
  }
}

@keyframes head-pulse {
  0%,
  100% {
    transform: translate(-50%, 0) scale(1);
  }
  50% {
    transform: translate(-50%, 0) scale(1.03);
  }
}

@keyframes title-in {
  from {
    opacity: 0;
    transform: translateY(22px) scale(0.88);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .glyphs,
  .ring,
  .embers span,
  .in .seal,
  .in .penta,
  .in .piece,
  .awake .flash,
  .awake .stage,
  .title .titles,
  .title .piece.head,
  .title .flare {
    animation: none;
  }

  .piece,
  .seal,
  .penta,
  .titles {
    opacity: 1;
    transform: none;
  }

  .piece.head {
    transform: translate(-50%, 0);
  }
}
</style>
